<script setup lang="ts">
    import GenericDropdownCheckbox from '@/shared/components/GenericDropdownCheckbox.vue';
    import GenericDropdownMenu from '@/shared/components/GenericDropdownMenu.vue';
    import Spinner from '@/shared/components/Spinner.vue';
    import {
        createPublishingRelease,
        getInternalApplicationSubscriptions,
        getLibraries,
        getMyLibraryAccess,
        getPublishCount,
        PublishCountRequest,
        PublishCountResponse,
        sqlQuery,
        UserLibraryAccess
    } from '@/shared/helpers/api';
    import { getReleaseLink } from '@/shared/helpers/routing';
    import { publishesToStidGlobalScope } from '@/shared/helpers/publishScope';
    import { http } from '@/shared/httpWrapper';
    import { isAdministrator } from '@/shared/directives/requirePermission';
    import useShow from '@/shared/composables/useShow';
    import moment from 'moment';
    import { computed, onMounted, reactive, ref, watch } from 'vue';
    import { useRouter } from 'vue-router';
    import PublisherPreview from './PublisherPreview.vue';
    import { cancelPreviousWhenCalledWithDebouncing } from '@/shared/helpers/utils';
    import type { Library, PublishFilter } from '@/shared/helpers/api/types/entities.ts';

    const router = useRouter();
    const { showError } = useShow();

    const receivers = ['STID', 'TIE'];
    const filters = ['IsValid', 'DateUpdated'];

    const libraryGroups = ref<string[]>([]);
    const facilities = ref<string[]>([]);
    const libraries = ref<Library[]>([]);
    const userLibraryAccess = ref<UserLibraryAccess[]>([]);
    const isAdmin = ref(false);
    const isValid = ref<boolean | null>(true);
    const updatedSince = ref<Date | null>(new Date(Date.now()));
    const selectedReceiver = ref<string | null>(null);
    const selectedGroups = ref<string[]>([]);
    const selectedFacilities = ref<string[]>([]);
    const selectedLibraries = ref<string[]>([]);
    const selectedFilters = ref<string[]>([]);
    const selected = ref<PublishCountResponse | null>(null);
    const isPublishing = ref(false);
    const loadingPreview = ref(false);
    const loading = ref(false);
    const singleMsgMetadataAllLibraries = reactive<Record<string, boolean>>({});
    const librariesByReceiverType = ref<Library[]>([]);

    const filteredLibraryNames = computed(() => {
        let filtered: Library[];
        if (!selectedReceiver.value) {
            filtered = selectedGroups.value.length
                ? libraries.value
                    .filter(lib => lib.tags.filter(tag => selectedGroups.value.includes(tag)).length > 0)
                : libraries.value;
        } else {
            filtered = selectedGroups.value.length
                ? librariesByReceiverType.value
                    .filter(lib => lib.tags.filter(tag => selectedGroups.value.includes(tag)).length > 0)
                : librariesByReceiverType.value;
        }
        return filtered.map(l => l.name);
    });

    const selectedLibraryObjects = computed(() => {
        return libraries.value.filter(lib => selectedLibraries.value.includes(lib.name));
    });

    // Global libraries go to STID's GLOBAL scope; everything else is published once per selected facility.
    const librariesNeedingFacilities = computed(() => {
        return selectedLibraryObjects.value
            .filter(lib => !publishesToStidGlobalScope(lib, selectedReceiver.value));
    });

    const publishBlockedReason = computed(() => {
        if (selectedReceiver.value === null) {
            return 'Select a receiver.';
        }
        if (selectedLibraries.value.length === 0) {
            return 'Select at least one library.';
        }
        if (librariesNeedingFacilities.value.length > 0 && selectedFacilities.value.length === 0) {
            const names = librariesNeedingFacilities.value.map(lib => lib.name).join(', ');
            return `Select at least one facility to publish ${names}.`;
        }
        return null;
    });

    const canPublish = computed(() => {
        return publishBlockedReason.value === null;
    });

    const codesToPublish = computed(() => {
        return selected.value?.count ?? 0;
    });

    // A library the count query returned nothing for is still listed, so it never silently disappears.
    const previewData = computed(() => {
        if (!selectedReceiver.value || selectedLibraries.value.length === 0) {
            return null;
        }

        const counted = new Map((selected.value?.libraries ?? []).map(lib => [lib.name, lib]));
        const needsFacilities = new Set(librariesNeedingFacilities.value.map(lib => lib.name));
        const missingFacilitySelection = selectedFacilities.value.length === 0;

        return {
            count: codesToPublish.value,
            libraries: selectedLibraries.value.map(name => counted.get(name) ?? {
                name,
                count: 0,
                facilities: [],
                note: needsFacilities.has(name) && missingFacilitySelection
                    ? 'Select at least one facility to publish this library.'
                    : 'Nothing matches the selected filters.'
            })
        };
    });

    const isValidFilterEnabled = computed(() => {
        return selectedFilters.value.includes('IsValid');
    });

    const updatedSinceFilterEnabled = computed(() => {
        return selectedFilters.value.includes('DateUpdated');
    });

    const loadPreview = cancelPreviousWhenCalledWithDebouncing(
        async function(abortSignal: AbortSignal, readyPromise: Promise<void>) {
            const applicationName = selectedReceiver.value;
            if (selectedLibraries.value.length === 0 || !applicationName) {
                selected.value = null;
                loadingPreview.value = false;
                return;
            }

            loadingPreview.value = true;
            await readyPromise;
            const request: PublishCountRequest = {
                applicationName,
                libraries: selectedLibraries.value,
                facilities: selectedFacilities.value,
                isValid: isValidFilterEnabled.value ? isValid.value : null,
                updatedSince: updatedSinceFilterEnabled.value && updatedSince.value
                    ? updatedSince.value.toISOString()
                    : null
            };
            const data = (await getPublishCount(http, request)).data;

            if (abortSignal.aborted) {
                return;
            }

            selected.value = data;
            loadingPreview.value = false;
        },
        { delay: 500 }
    );

    watch(
        () => [
            selectedReceiver.value,
            selectedLibraries.value,
            selectedFacilities.value,
            selectedFilters.value,
            isValid.value,
            updatedSince.value
        ],
        () => {
            loadPreview();
        },
        { deep: true }
    );

    watch(selectedReceiver, async () => {
        if (selectedReceiver.value) {
            facilities.value = await getFacilities();
            const subscriptions = await getInternalApplicationSubscriptions(http, selectedReceiver.value);
            librariesByReceiverType.value = libraries.value.filter((library) => {
                return subscriptions.find((subscription) => {
                    return subscription.library.name === library.name && subscription.enabled;
                });
            });
        }
    });

    onMounted(async () => {
        loading.value = true;

        isAdmin.value = await isAdministrator();

        const loadLibraries = async () => {
            const libs = await getLibraries(http);
            libraries.value = libs.sort((a, b) => a.name.localeCompare(b.name));
        };

        const loadAccess = async () => {
            if (!isAdmin.value) {
                userLibraryAccess.value = await getMyLibraryAccess(http);
            }
        };

        await Promise.all([
            loadLibraries(),
            loadAccess()
        ]);

        if (!isAdmin.value) {
            const accessibleLibraryNames = new Set(
                userLibraryAccess.value
                    .filter(a => a.codeEdit !== null)
                    .map(a => a.libraryName)
            );
            libraries.value = libraries.value.filter(lib => accessibleLibraryNames.has(lib.name));
        }

        libraryGroups.value = [...new Set(libraries.value.flatMap(lib => lib.tags))].sort();

        await loadLibrariesMetadata();

        loading.value = false;
    });

    async function publish() {
        isPublishing.value = true;

        const specification: Partial<PublishFilter> = {
            applicationName: selectedReceiver.value,
            libraryNames: selectedLibraries.value,
            facilityNames: selectedFacilities.value,
            codesCount: codesToPublish.value
        };
        if (selectedReceiver.value === 'TIE') {
            const singleMsgEnabledMetadata: Record<string, boolean> = {};
            selectedLibraries.value.forEach(selectedLib => {
                singleMsgEnabledMetadata[selectedLib] = singleMsgMetadataAllLibraries[selectedLib];
            });
            specification.singleMsgEnabledMetadata = singleMsgEnabledMetadata;
        }
        if (isValidFilterEnabled.value) {
            specification.isValid = true;
        }
        if (updatedSinceFilterEnabled.value && updatedSince.value) {
            specification.updatedSince = updatedSince.value.toISOString();
        }
        const response = await createPublishingRelease(http, specification as PublishFilter);

        isPublishing.value = false;

        if (response.status === 200 && response.data.id) {
            await router.push(getReleaseLink(response.data.id));
        } else {
            showError('Could not publish selected libraries');
        }
    }

    async function getFacilities(): Promise<string[]> {
        const query = {
            TIE: 'SELECT [Name] FROM [Facility] WHERE [IsValid] = 1 AND [IsForTIELibraryPublish] = 1',
            STID: 'SELECT [Name] FROM [Facility] WHERE [IsValid] = 1 AND [IsForSTID] = 1'
        }[selectedReceiver.value!]!;
        const result = (await sqlQuery(http, { query })).data as { Name: string }[];
        return result.map(fac => fac.Name).sort();
    }

    function formatDatetime(dateTime: Date): string {
        return moment(dateTime).format('YYYY-MM-DD HH:mm:ss');
    }

    function parseDatetime(string: string): Date {
        return moment(string).toDate();
    }

    function onToggleIsSingleMessageEnabled(libname: string, isEnabled: boolean) {
        singleMsgMetadataAllLibraries[libname] = isEnabled;
    }

    async function loadLibrariesMetadata() {
        const metadataList = (await sqlQuery(http, {
            query: 'SELECT [Name], [UseSingleObjectTIEMessages] FROM [LibraryMetadata] WHERE [IsValid] = 1'
        })).data as { Name: string; UseSingleObjectTIEMessages: boolean }[];
        libraries.value.forEach(library => {
            const singleMsgEnabledMetadata = metadataList
                ?.find(metadata => metadata.Name === library.name);
            singleMsgMetadataAllLibraries[library.name] = singleMsgEnabledMetadata
                ?.UseSingleObjectTIEMessages ?? false;
        });
    }
</script>

<template>
    <div
        class="container"
        style="max-width: 1280px">
        <h1 class="title">
            Publisher <span class="icon is-small has-text-info pl-4"><i class="mdi mdi-rocket-launch" /></span>
        </h1>
        <h2 class="subtitle">
            Publishing of Code Sets
        </h2>
        <spinner :loading="loading">
            <div class="buttons block">
                <generic-dropdown-menu
                    :alternatives="receivers"
                    :button-type="'is-info'"
                    :none-selected-text="'Select receiver'"
                    :selected-text-prefix="'Receiver: '"
                    @setSelected="(newValue: string) => selectedReceiver = newValue" />
                <generic-dropdown-checkbox
                    v-model="selectedGroups"
                    :options="libraryGroups"
                    title="Library groups"
                    :show-selected-count="selectedGroups.length > 0" />
                <generic-dropdown-checkbox
                    v-model="selectedLibraries"
                    :options="filteredLibraryNames"
                    title="Libraries" />
                <generic-dropdown-checkbox
                    v-model="selectedFacilities"
                    :options="facilities"
                    :disabled="facilities.length === 0"
                    title="Facilities" />
                <generic-dropdown-checkbox
                    v-model="selectedFilters"
                    :options="filters"
                    title="Filters" />
                <b-tooltip
                    style="margin-left: auto"
                    :active="!!publishBlockedReason"
                    :label="publishBlockedReason ?? ''"
                    position="is-left"
                    multilined>
                    <b-button
                        class="button is-primary"
                        :disabled="!canPublish"
                        :loading="isPublishing"
                        @click="publish">
                        <span class="icon is-small">
                            <i class="mdi mdi-play" />
                        </span>
                        <span>
                            Publish {{ codesToPublish }} codes
                        </span>
                    </b-button>
                </b-tooltip>
            </div>

            <section class="block">
                <h3 class="title is-4">
                    Filters
                </h3>
                <b-field
                    v-if="selectedFilters.length > 0"
                    grouped>
                    <b-field
                        v-if="isValidFilterEnabled"
                        label="IsValid =">
                        <b-select
                            v-model="isValid"
                            placeholder="No filter">
                            <option :value="null" />
                            <option :value="true">
                                True
                            </option>
                            <option :value="false">
                                False
                            </option>
                        </b-select>
                    </b-field>
                    <b-field
                        v-if="updatedSinceFilterEnabled"
                        class="date-updated-field"
                        label="DateUpdated (UTC) ≥">
                        <b-datetimepicker
                            v-model="updatedSince"
                            placeholder="Type or select a date..."
                            icon="calendar-today"
                            :icon-right="updatedSince ? 'close-circle' : ''"
                            icon-right-clickable
                            :datetime-formatter="formatDatetime"
                            :datetime-parser="parseDatetime"
                            :tz-offset="new Date().getTimezoneOffset()"
                            :timepicker="{ enableSeconds: true, hourFormat: '24' }"
                            editable
                            @icon-right-click="updatedSince = null" />
                    </b-field>
                </b-field>
                <p v-else>
                    All codes will be published.
                </p>
            </section>

            <publisher-preview
                :data="previewData"
                :is-loading="loadingPreview"
                :receiver="selectedReceiver"
                :filters="selectedFilters"
                :single-msg-metadata-all-libraries="singleMsgMetadataAllLibraries"
                @toggleIsSingleMessageEnabled="onToggleIsSingleMessageEnabled" />
        </spinner>
    </div>
</template>

<style scoped>
/* space for icon + input text + icon */
.date-updated-field {
  min-width: calc(32px + 23ch + 24px);
}
</style>
